// Pure presentation logic for the sub-vendo wizard's final ("Done") step.
//
// Extracted so it can be unit-tested without mounting the whole wizard
// (which needs the full react-query + store provider stack). The wizard's
// success framing MUST reflect the ROUTER PUSH result, not just that
// registerDevice succeeded — otherwise a failed VLAN push still shows a green
// check + an enabled "Done", and the operator closes on a half-applied
// sub-vendo. See vendo-lifecycle-fixes-plan.md (Fix 1).

export type PushStatus = 'idle' | 'pushing' | 'success' | 'failed';

export interface SubVendoDoneView {
   tone: 'pending' | 'success' | 'error';
   icon: string;
   title: string;
   /** True while the router config is being pushed — Done must be disabled. */
   pushPending: boolean;
   /** True when the push failed — show warning state + Retry, not success. */
   pushFailed: boolean;
   /** The primary button is disabled (and labelled "Pushing…") while pending. */
   doneDisabled: boolean;
   /** Show the Retry-push + Close pair instead of a single Done. */
   showRetry: boolean;
}

/**
 * Whether a sub-vendo's setup is genuinely complete — i.e. it's safe to mark
 * `setupStatus = 'done'` so it nests under its parent. Only true at the final
 * step AND when nothing is still pending:
 *  - VLAN subs (canAutoPush): the router push must have SUCCEEDED.
 *  - flat-LAN / dev subs (no push): reaching the final step is the finish.
 *
 * If this is false, the sub stays `setupStatus != 'done'` → shows in "Setup in
 * progress" → can be resumed (wizard closed midway, or push failed).
 */
export function isSubVendoSetupComplete(
   atFinalStep: boolean,
   canAutoPush: boolean,
   pushStatus: PushStatus
): boolean {
   if (!atFinalStep) return false;
   return canAutoPush ? pushStatus === 'success' : true;
}

export function subVendoDoneView(
   canAutoPush: boolean,
   pushStatus: PushStatus
): SubVendoDoneView {
   const pushPending = canAutoPush && pushStatus === 'pushing';
   const pushFailed = canAutoPush && pushStatus === 'failed';
   const tone: SubVendoDoneView['tone'] = pushFailed
      ? 'error'
      : pushPending
      ? 'pending'
      : 'success';
   return {
      tone,
      icon: pushFailed ? '!' : pushPending ? '…' : '✓',
      title: pushFailed
         ? 'Registered — router push failed'
         : pushPending
         ? 'Finishing setup…'
         : 'Sub-vendo ready',
      pushPending,
      pushFailed,
      doneDisabled: pushPending,
      showRetry: pushFailed,
   };
}

export type SubVendoApplyMode = 'push' | 'manual' | 'pull' | 'none';

/**
 * How the parent router gets the new sub-vendo's network after registration.
 *  - push:   MikroTik in prod — the wizard calls the REST apply and shows its result.
 *  - manual: MikroTik in dev — no auto-push (it would provision a prod WG peer);
 *            the operator pastes the script.
 *  - pull:   OpenWRT (OrangePi) — nothing to push to; the box reads the layout
 *            on its next heartbeat and acks it.
 *  - none:   flat-LAN sub — rides the parent's existing network, no change needed.
 */
export function subVendoApplyMode(
   parentPlatform: 'mikrotik' | 'openwrt' | undefined,
   vlanInPlay: boolean,
   isDev: boolean
): SubVendoApplyMode {
   if (!vlanInPlay) return 'none';
   // An OpenWRT parent is pull-based in every environment: there is no REST
   // API to push to, and the dev-mode WG-peer concern is a MikroTik one.
   if (parentPlatform === 'openwrt') return 'pull';
   return isDev ? 'manual' : 'push';
}
